import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, HardDrive, Calendar, Tag, X, Upload, CheckCircle, Clock, AlertCircle, Copy } from "lucide-react";
import { useAuthContext } from "../../contexts/AuthContext";
import { PRO_ADDONS } from "../../config/subscriptionPlans";
import { db, auth } from "../../lib/firebase";
import { collection, query, where, getDocs, addDoc, Timestamp, doc, getDoc } from "firebase/firestore";

export default function AddonCheckout() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [selectedAddon, setSelectedAddon] = useState(PRO_ADDONS[1]); // Default to +25GB
  const [isYearly, setIsYearly] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [appliedDiscount, setAppliedDiscount] = useState<{ code: string; type: string; value: number } | null>(null);
  const [discountError, setDiscountError] = useState("");
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  
  // Payment states
  const [paymentRef, setPaymentRef] = useState<string>('');
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'uploaded' | 'verifying'>('pending');
  const [copied, setCopied] = useState(false);
  const [expiryTime, setExpiryTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<{
    promptPayNumber: string;
    accountName: string;
    qrCodeUrl: string;
  } | null>(null);

  const PROMPTPAY_NUMBER = paymentSettings?.promptPayNumber || '0812345678';
  const PROMPTPAY_NAME = paymentSettings?.accountName || 'EduMi Co., Ltd.';

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    
    loadPaymentSettings();
    
    if (showPaymentForm && !paymentRef) {
      const expiry = new Date(Date.now() + 30 * 60 * 1000);
      setExpiryTime(expiry);
      const ref = `AD${Date.now()}${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
      setPaymentRef(ref);
    }
  }, [user, navigate, showPaymentForm, paymentRef]);

  const loadPaymentSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'paymentSettings', 'default'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setPaymentSettings({
          promptPayNumber: data.promptpay?.id || '0812345678',
          accountName: data.promptpay?.name || 'EduMi Co., Ltd.',
          qrCodeUrl: data.promptpay?.qrCodeUrl || ''
        });
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  useEffect(() => {
    if (!expiryTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const diff = expiryTime.getTime() - now.getTime();
      
      if (diff <= 0) {
        setTimeRemaining('หมดเวลา');
        clearInterval(interval);
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryTime]);

  if (!user) {
    return null;
  }

  const basePrice = isYearly ? selectedAddon.priceYearly : selectedAddon.price;
  const discountAmount = appliedDiscount 
    ? appliedDiscount.type === "percentage"
      ? Math.round(basePrice * appliedDiscount.value / 100)
      : appliedDiscount.value
    : 0;
  const price = basePrice - discountAmount;
  const period = isYearly ? "ต่อปี" : "ต่อเดือน";
  const savings = isYearly ? selectedAddon.price * 12 - selectedAddon.priceYearly : 0;

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError("กรุณากรอกรหัสส่วนลด");
      return;
    }

    setCheckingDiscount(true);
    setDiscountError("");

    try {
      // Query discount from subscription_discounts collection
      const discountQuery = query(
        collection(db, "subscription_discounts"),
        where("code", "==", discountCode.trim().toUpperCase())
      );
      const discountSnap = await getDocs(discountQuery);

      if (discountSnap.empty) {
        setDiscountError("โค้ดส่วนลดไม่ถูกต้องหรือหมดอายุ");
        setAppliedDiscount(null);
        return;
      }

      const discountData = discountSnap.docs[0].data();

      // Check if discount is active
      if (!discountData.isActive) {
        setDiscountError("โค้ดส่วนลดนี้ถูกปิดใช้งานแล้ว");
        setAppliedDiscount(null);
        return;
      }

      // Check usage limit
      if (discountData.usageLimit && discountData.usedCount >= discountData.usageLimit) {
        setDiscountError("โค้ดส่วนลดนี้ถูกใช้งานครบแล้ว");
        setAppliedDiscount(null);
        return;
      }

      // Check expiry date
      if (discountData.expiryDate) {
        const expiryDate = discountData.expiryDate.toDate();
        if (expiryDate < new Date()) {
          setDiscountError("โค้ดส่วนลดนี้หมดอายุแล้ว");
          setAppliedDiscount(null);
          return;
        }
      }

      // Apply discount
      setAppliedDiscount({
        code: discountData.code,
        type: discountData.type,
        value: discountData.value,
      });
      setDiscountError("");
    } catch (error) {
      console.error("Error checking discount:", error);
      setDiscountError("เกิดข้อผิดพลาดในการตรวจสอบโค้ดส่วนลด");
      setAppliedDiscount(null);
    } finally {
      setCheckingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode("");
    setDiscountError("");
  };

  const handleSlipUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('กรุณาอัปโหลดไฟล์รูปภาพเท่านั้น');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('ไฟล์ใหญ่เกิน 5MB');
      return;
    }

    setSlipFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setSlipPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async () => {
    if (!slipFile || !user) {
      alert('กรุณาอัปโหลดสลิปการโอนเงิน');
      return;
    }

    setIsUploading(true);
    setPaymentStatus('verifying');

    try {
      console.log('[Payment] Uploading slip to R2...');
      
      // Get Firebase ID token from auth.currentUser
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('ไม่พบข้อมูลผู้ใช้ กรุณาล็อกอินใหม่');
      }
      const idToken = await currentUser.getIdToken(true);
      
      // Upload to Cloudflare R2 via Worker
      const formData = new FormData();
      formData.append('file', slipFile);
      formData.append('fileName', `payment-slips/addon/${user.uid}/${Date.now()}_${slipFile.name}`);
      
      const uploadResponse = await fetch('https://edumi-upload-api.edumi-subdomin.workers.dev', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('[Payment] Upload error:', errorText);
        throw new Error('ไม่สามารถอัปโหลดสลิปได้');
      }

      const uploadResult = await uploadResponse.json();
      const slipUrl = uploadResult.publicUrl;
      
      console.log('[Payment] Slip uploaded successfully:', slipUrl);

      const paymentRecord = {
        userId: user.uid,
        email: user.email || '',
        type: 'addon',
        addonId: selectedAddon.id,
        addonName: selectedAddon.name,
        storageGB: selectedAddon.storageGB,
        amount: price,
        originalAmount: basePrice,
        discountAmount: discountAmount,
        discountCode: appliedDiscount?.code || null,
        billingPeriod: isYearly ? 'yearly' : 'monthly',
        status: 'pending',
        paymentMethod: 'promptpay',
        promptPayRef: paymentRef,
        slipImageUrl: slipUrl,
        createdAt: Timestamp.now(),
        verifiedAt: null,
        expiresAt: Timestamp.fromDate(expiryTime!),
      };

      await addDoc(collection(db, 'addonPayments'), paymentRecord);

      console.log('✅ Payment record saved successfully');
      
      setPaymentStatus('uploaded');
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('Error submitting payment:', error);
      alert('เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      setPaymentStatus('pending');
    } finally {
      setIsUploading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const generatePromptPayQR = () => {
    // If custom QR code is uploaded, use it
    if (paymentSettings?.qrCodeUrl) {
      return paymentSettings.qrCodeUrl;
    }
    // Otherwise generate from PromptPay API
    return `https://promptpay.io/${PROMPTPAY_NUMBER}/${price}.png`;
  };

  const handleProceedToPayment = () => {
    setShowPaymentForm(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-4 md:py-8 px-3 md:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <Link to="/instructor/storage">
          <button className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 md:mb-6 font-semibold text-sm md:text-base">
            <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
            กลับ
          </button>
        </Link>

        {/* Header */}
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-1 md:mb-2">
            เพิ่มพื้นที่จัดเก็บ (Add-on)
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            เลือกขนาดพื้นที่เพิ่มเติมสำหรับแพ็กเกจของคุณ
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-8">
          {/* Left: Storage Selection or Payment Form */}
          <div className="md:col-span-2">
            {showPaymentForm ? (
              // Payment Form
              <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">ชำระเงิน</h2>
                  {expiryTime && (
                    <div className="flex items-center gap-1 md:gap-2 text-blue-600">
                      <Clock className="w-4 h-4 md:w-5 md:h-5" />
                      <span className="text-sm md:text-base font-semibold">{timeRemaining}</span>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-pink-600 rounded-xl md:rounded-2xl p-4 md:p-8 text-white mb-4 md:mb-6">
                  <div className="text-center mb-4 md:mb-6">
                    <div className="w-48 h-48 md:w-64 md:h-64 mx-auto bg-white p-3 md:p-4 rounded-xl">
                      <img
                        src={generatePromptPayQR()}
                        alt="PromptPay QR Code"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xs md:text-sm opacity-90 mb-1">จำนวนเงิน</div>
                    <div className="text-3xl md:text-4xl font-bold mb-2 md:mb-4">฿{price.toLocaleString()}</div>
                    <div className="text-xs md:text-sm opacity-90">
                      {isYearly ? 'ชำระรายปี' : 'ชำระรายเดือน'}
                    </div>
                  </div>
                </div>

                <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                  <div className="bg-gray-50 rounded-lg md:rounded-xl p-3 md:p-4">
                    <div className="flex items-center justify-between mb-1 md:mb-2">
                      <span className="text-xs md:text-sm text-gray-600">เลขพร้อมเพย์</span>
                      <button
                        onClick={() => copyToClipboard(PROMPTPAY_NUMBER)}
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-xs md:text-sm"
                      >
                        {copied ? (
                          <>
                            <Check className="w-3 h-3 md:w-4 md:h-4" />
                            <span className="hidden md:inline">คัดลอกแล้ว</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3 md:w-4 md:h-4" />
                            <span className="hidden md:inline">คัดลอก</span>
                          </>
                        )}
                      </button>
                    </div>
                    <div className="text-base md:text-lg font-bold text-gray-900">{PROMPTPAY_NUMBER}</div>
                  </div>

                  <div className="bg-gray-50 rounded-lg md:rounded-xl p-3 md:p-4">
                    <div className="text-xs md:text-sm text-gray-600 mb-1">ชื่อบัญชี</div>
                    <div className="text-base md:text-lg font-bold text-gray-900">{PROMPTPAY_NAME}</div>
                  </div>

                  <div className="bg-gray-50 rounded-lg md:rounded-xl p-3 md:p-4">
                    <div className="flex items-center justify-between mb-1 md:mb-2">
                      <span className="text-xs md:text-sm text-gray-600">เลขอ้างอิง</span>
                      <button
                        onClick={() => copyToClipboard(paymentRef)}
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-xs md:text-sm"
                      >
                        <Copy className="w-3 h-3 md:w-4 md:h-4" />
                        <span className="hidden md:inline">คัดลอก</span>
                      </button>
                    </div>
                    <div className="text-base md:text-lg font-bold text-gray-900 font-mono break-all">{paymentRef}</div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-blue-200 mb-4 md:mb-6">
                  <div className="flex items-start gap-2 md:gap-3">
                    <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs md:text-sm text-blue-800">
                      <div className="font-semibold mb-1">วิธีการชำระเงิน:</div>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>สแกน QR Code หรือโอนเงินผ่าน PromptPay</li>
                        <li>ใส่เลขอ้างอิงในช่องหมายเหตุ</li>
                        <li>อัปโหลดสลิปการโอนเงินด้านล่าง</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Slip Upload */}
                <div className="border-t pt-4 md:pt-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4">อัปโหลดสลิปการโอนเงิน</h3>
                  
                  {paymentStatus === 'pending' && (
                    <div className="space-y-3 md:space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg md:rounded-xl p-4 md:p-8 text-center hover:border-blue-500 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleSlipUpload}
                          className="hidden"
                          id="slip-upload"
                        />
                        <label htmlFor="slip-upload" className="cursor-pointer block">
                          {slipPreview ? (
                            <div>
                              <img
                                src={slipPreview}
                                alt="Payment Slip Preview"
                                className="max-w-full max-h-48 md:max-h-64 mx-auto rounded-lg mb-3 md:mb-4"
                              />
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSlipFile(null);
                                  setSlipPreview(null);
                                }}
                                className="text-red-600 hover:text-red-700 text-xs md:text-sm font-medium"
                              >
                                ลบรูป
                              </button>
                            </div>
                          ) : (
                            <div>
                              <Upload className="w-10 h-10 md:w-12 md:h-12 text-gray-400 mx-auto mb-3 md:mb-4" />
                              <p className="text-sm md:text-base text-gray-700 font-medium mb-1 md:mb-2">คลิกเพื่ออัปโหลดสลิป</p>
                              <p className="text-xs md:text-sm text-gray-500">รองรับไฟล์ JPG, PNG (สูงสุด 5MB)</p>
                            </div>
                          )}
                        </label>
                      </div>

                      <button
                        onClick={handleSubmitPayment}
                        disabled={!slipFile || isUploading}
                        className="w-full px-4 md:px-6 py-3 md:py-4 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-lg md:rounded-xl font-semibold text-base md:text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isUploading ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span className="text-sm md:text-base">กำลังอัปโหลด...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm md:text-base">ยืนยันการชำระเงิน</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {paymentStatus === 'uploaded' && (
                    <div className="py-6 md:py-8">
                      <div className="text-center mb-6">
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                          <CheckCircle className="w-10 h-10 md:w-12 md:h-12 text-green-600" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                          ได้รับการชำระเงินแล้ว!
                        </h3>
                        <p className="text-sm md:text-base text-gray-600 mb-1">
                          เราได้รับสลิปการโอนเงินของคุณเรียบร้อยแล้ว
                        </p>
                        <p className="text-xs md:text-sm text-gray-500">
                          เลขอ้างอิง: <span className="font-mono font-semibold">{paymentRef}</span>
                        </p>
                      </div>

                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 md:p-6 border-2 border-blue-200 mb-6">
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 md:w-6 md:h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <h4 className="font-bold text-blue-900 mb-2 text-sm md:text-base">
                              ขั้นตอนต่อไป
                            </h4>
                            <ul className="space-y-2 text-xs md:text-sm text-blue-800">
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600 font-bold">1.</span>
                                <span>Admin จะตรวจสอบและอนุมัติการชำระเงินภายใน <strong>24 ชั่วโมง</strong></span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600 font-bold">2.</span>
                                <span>คุณจะได้รับอีเมลยืนยันเมื่อการชำระเงินได้รับการอนุมัติ</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-600 font-bold">3.</span>
                                <span>พื้นที่เพิ่มเติม <strong>{selectedAddon.name}</strong> จะถูกเพิ่มให้อัตโนมัติทันที</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="bg-green-50 rounded-xl p-4 border border-green-200 mb-6">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <h4 className="font-semibold text-green-900 text-sm md:text-base">รายละเอียดการชำระเงิน</h4>
                        </div>
                        <div className="space-y-1 text-xs md:text-sm text-green-800">
                          <div className="flex justify-between">
                            <span>จำนวนเงิน:</span>
                            <span className="font-bold">฿{price.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>แพ็กเกจ:</span>
                            <span className="font-semibold">{selectedAddon.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>รอบชำระ:</span>
                            <span className="font-semibold">{isYearly ? 'รายปี' : 'รายเดือน'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <button
                          onClick={() => navigate('/instructor/storage')}
                          className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold text-sm md:text-base hover:shadow-xl transition-all flex items-center justify-center gap-2"
                        >
                          <HardDrive className="w-5 h-5" />
                          ไปหน้า Storage Dashboard
                        </button>
                        <button
                          onClick={() => navigate('/instructor/dashboard')}
                          className="w-full px-4 py-3 bg-white text-gray-700 rounded-xl font-semibold text-sm md:text-base hover:bg-gray-50 transition-all flex items-center justify-center gap-2 border-2 border-gray-200"
                        >
                          ไปหน้า Dashboard
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Storage Selection Form
              <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8">
              <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                <HardDrive className="w-5 h-5 text-blue-600" />
                เลือกขนาดพื้นที่
              </h2>

              <div className="space-y-2 mb-6">
                {PRO_ADDONS.map((addon) => (
                  <button
                    key={addon.id}
                    onClick={() => setSelectedAddon(addon)}
                    className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-between ${
                      selectedAddon.id === addon.id
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedAddon.id === addon.id
                          ? "border-blue-600 bg-blue-600"
                          : "border-gray-300"
                      }`}>
                        {selectedAddon.id === addon.id && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div>
                        <span className="font-bold text-gray-800">{addon.name}</span>
                        <span className="text-gray-500 text-sm ml-2">{addon.description}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-blue-600">
                        ฿{addon.price.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">/ เดือน</div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Billing Period Toggle */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  รอบการชำระเงิน
                </h3>

                <div className="space-y-2">
                  <button
                    onClick={() => setIsYearly(false)}
                    className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-between ${
                      !isYearly
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        !isYearly
                          ? "border-blue-600 bg-blue-600"
                          : "border-gray-300"
                      }`}>
                        {!isYearly && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <span className="font-semibold text-gray-800">รายเดือน</span>
                    </div>
                    <div className="font-bold text-blue-600">
                      ฿{selectedAddon.price.toLocaleString()}/เดือน
                    </div>
                  </button>

                  <button
                    onClick={() => setIsYearly(true)}
                    className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left flex items-center justify-between ${
                      isYearly
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isYearly
                          ? "border-blue-600 bg-blue-600"
                          : "border-gray-300"
                      }`}>
                        {isYearly && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div>
                        <span className="font-semibold text-gray-800">รายปี</span>
                        <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                          ประหยัด ฿{savings.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-blue-600">
                        ฿{selectedAddon.priceYearly.toLocaleString()}/ปี
                      </div>
                      <div className="text-xs text-gray-500">ฟรี 2 เดือน</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Right: Order Summary */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-4 md:p-8 md:sticky md:top-24">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">
                สรุปรายการ
              </h2>

              <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-800">พื้นที่เพิ่มเติม</p>
                    <p className="text-sm text-gray-600">{selectedAddon.name}</p>
                  </div>
                  <p className="font-bold text-gray-800">
                    ฿{basePrice.toLocaleString()}
                  </p>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <p className="text-gray-600">รอบการชำระเงิน</p>
                  <p className="font-semibold text-gray-800">{period}</p>
                </div>

                {isYearly && (
                  <div className="flex justify-between items-center text-sm bg-green-50 p-3 rounded-lg">
                    <p className="text-green-700 font-semibold">ส่วนลดรายปี</p>
                    <p className="text-green-700 font-bold">
                      -฿{savings.toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Discount Code Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Tag className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-semibold text-gray-800">โค้ดส่วนลด</p>
                  </div>
                  
                  {!appliedDiscount ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                          placeholder="ใส่โค้ดส่วนลด"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={handleApplyDiscount}
                          disabled={!discountCode.trim() || checkingDiscount}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          {checkingDiscount ? "กำลังตรวจสอบ..." : "ใช้"}
                        </button>
                      </div>
                      {discountError && (
                        <p className="text-xs text-red-500">{discountError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-green-50 p-3 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-semibold text-green-700">
                            {appliedDiscount.code}
                          </span>
                          <span className="text-xs text-green-600">
                            {appliedDiscount.type === "percentage" 
                              ? `(-${appliedDiscount.value}%)` 
                              : `(-฿${appliedDiscount.value.toLocaleString()})`}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-green-700">
                            -฿{discountAmount.toLocaleString()}
                          </span>
                          <button
                            onClick={handleRemoveDiscount}
                            className="text-green-600 hover:text-green-800 transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <p className="text-lg font-bold text-gray-800">ยอดรวม</p>
                  <p className="text-3xl font-bold text-blue-600">
                    ฿{price.toLocaleString()}
                  </p>
                </div>
                <p className="text-xs text-gray-500 text-right mt-1">{period}</p>
              </div>

              <div className="space-y-3 mb-6 text-sm">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">เริ่มใช้งานทันที</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">ยกเลิกได้ทุกเมื่อ</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">ต่ออายุอัตโนมัติ</span>
                </div>
              </div>

              {!showPaymentForm ? (
                <button
                  onClick={handleProceedToPayment}
                  className="w-full bg-gradient-to-r from-blue-600 to-pink-600 text-white py-3 md:py-4 rounded-lg md:rounded-xl font-bold text-sm md:text-base hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                  ดำเนินการชำระเงิน
                </button>
              ) : (
                <div className="text-xs md:text-sm text-gray-500 text-center">
                  กรุณาชำระเงินและอัปโหลดสลิปด้านบน
                </div>
              )}

              <p className="text-xs text-gray-500 text-center mt-4">
                การชำระเงินปลอดภัยด้วย SSL
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
